import axios from "axios";
import React, { useState, useEffect } from "react";
import { Box, Flex, Tabs, Title, Card } from "@mantine/core";
import { useParams, useMatch, Route, Routes, Outlet } from "react-router-dom";
import { useSelector } from "react-redux";

import { AssetManagement, LogoUpload } from "@components/Asset";
import { CampaignManagement } from "@components/Campaign";
import { ContestManagement } from "@components/Contest";
import { EffortManagement } from "@components/Effort";
import { KeywordManagement } from "@components/Keyword";
import { LocationAssociate, LocationGroup } from "@components/Location";
import { LootLockerManagement } from "@components/LootLocker";
import { ManagerManagement } from "@components/Manager";
import { OrganizationForm } from "@components/Organization";
import {
  ErrorView,
  EntitySettingsCode,
  PageTitle,
  QrCodeGenerator,
  RequestApiConnectionButton,
  Subnav,
} from "@components/shared";
import { EmailServerCreate } from "@components/EmailServer";
import { MessageList } from "@components/Message";
import {
  BulkCommunicationManagement,
  BulkCommunicationAllowToggle,
  EntityMessagingSms,
} from "@components/BulkCommunication";
import { EmailAppConfig } from "@components/AppConfig";
import { UserGroupManagement } from "@components/UserGroup";
import { AffiliateJoinManagement } from "@components/Affiliate";

import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";

export default function OrganizationDetail({ isProfile }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const params = useParams();
  const adminInfo = useSelector((state) => state.admin);
  const isAdmin = adminInfo ? true : false;

  const { id } = params;

  useEffect(() => {
    fetchData();
  }, []);

  function fetchData() {
    axios
      .get(`/organizations/${id}/`)
      .then(({ data }) => {
        setInfo(data.response[0]);
      })
      .catch((err) => {
        setError(err);
        setInfo(null);
      });
  }

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!info) return null;

  const postmarkServerId =
    info.postmark_servers && JSON.stringify(info.postmark_servers) !== "{}"
      ? Object.keys(info.postmark_servers)[0]
      : null;

  const showSms =
    (info.allow_sms_marketing || info.allow_transactional_sms) && isAdmin;

  const links = [
    { to: `/organizations/${id}`, text: "Detail" },
    {
      title: "Location Features",
      links: [
        { to: `/organizations/${id}/locations`, text: "Locations" },
        { to: `/organizations/${id}/location-groups`, text: "Location Groups" },
      ],
      visible: !isProfile,
    },
    {
      title: "Interaction",
      visible: !isProfile,
      links: [
        { to: `/organizations/${id}/campaigns`, text: "Campaigns" },
        { to: `/organizations/${id}/efforts`, text: "Efforts" },
        { to: `/organizations/${id}/contests`, text: "Contests" },
        { to: `/organizations/${id}/keywords`, text: "Keywords" },
        {
          to: `/organizations/${id}/bulk-communication`,
          text: "Bulk Communication",
        },
        {
          to: `/postmark/${postmarkServerId}`,
          text: "Email",
          visible: postmarkServerId ? true : false,
        },
        {
          to: `/organizations/${id}/sms-messaging`,
          text: "SMS Messaging",
          visible: showSms ? true : false,
        },
      ].filter((f) => f.visible !== false),
    },
    {
      to: `/organizations/${id}/managers`,
      text: "Managers",
      visible: !isProfile,
    },
    {
      to: `/organizations/${id}/look-and-feel`,
      text: "Look & Feel",
      visible: true,
      // visible: postmarkServerId ? true : false,
    },
    {
      to: `/organizations/${id}/loot-locker`,
      text: "Loot Locker",
      visible: !isProfile,
    },
    {
      to: `/organizations/${id}/asset-library`,
      text: "Asset Library",
      visible: !isProfile,
    },
    {
      to: `/organizations/${id}/segments`,
      text: "Segments",
      visible: !isProfile,
    },
    {
      to: `/organizations/${id}/affiliates`,
      text: "Affiliates",
      visible: !isProfile,
    },
  ]
    .filter((f) => f.visible !== false)
    .filter((f) => (f.links ? f.links.length > 0 : true));

  return (
    <div>
      {info.third_party_ident && !adminInfo && (
        <Box mb="lg">
          <RequestApiConnectionButton organizationId={id} />
        </Box>
      )}
      <Flex mb="lg" gap="lg">
        <LogoUpload
          organizationId={id}
          onSuccess={fetchData}
          url={info.logo_url}
        />
        <PageTitle
          title={info.name}
          subtitle={<React.Fragment>Organization</React.Fragment>}
          icon={entityIcon.organization()}
          iconColor={entityColor.organization}
          style={{ marginBottom: "0" }}
        />
      </Flex>
      {links.length > 1 && (
        <Subnav buttonColor={entityColor.organization} links={links} />
      )}
      <Routes>
        <Route
          exact
          path="/"
          element={
            <React.Fragment>
              <Card>
                <OrganizationForm
                  address1={info.address_1}
                  address2={info.address_2}
                  allowCampaignCreate={info.allow_campaign_create}
                  city={info.city}
                  country={info.country}
                  id={id}
                  isProfile={isProfile}
                  keywordCode={info.keyword_code}
                  manageLocations={info.manage_locations}
                  name={info.name}
                  onSuccess={fetchData}
                  otpDisplayName={info.otp_display_name}
                  state={info.state_province}
                  timezone={info.timezone}
                  thirdPartyIdent={info.third_party_ident}
                  zip={info.zip}
                />
              </Card>
              {!postmarkServerId && !isProfile && (
                <EmailServerCreate fetchData={fetchData} organizationId={id} />
              )}
              {isAdmin && (
                <div>
                  <Card mt="lg">
                    <BulkCommunicationAllowToggle
                      fetchData={fetchData}
                      organizationId={id}
                      value={info.allow_email_marketing}
                      disabled={postmarkServerId ? false : true}
                    />
                  </Card>
                  <Card mt="xs">
                    <BulkCommunicationAllowToggle
                      fetchData={fetchData}
                      organizationId={id}
                      value={info.allow_sms_marketing}
                      smsMarketing
                    />
                  </Card>
                  <Card mt="xs">
                    <BulkCommunicationAllowToggle
                      fetchData={fetchData}
                      organizationId={id}
                      value={info.allow_transactional_sms}
                      transactionalSms
                    />
                  </Card>
                  <Card mt="xs">
                    <BulkCommunicationAllowToggle
                      fetchData={fetchData}
                      organizationId={id}
                      value={info.allow_conversational_sms}
                      conversationalSms
                    />
                  </Card>
                </div>
              )}
              {info.microsite_gallery_code && (
                <>
                  <Title mt="xl" mb="xs" order={4}>
                    Gallery QR Code
                  </Title>
                  <Card>
                    <QrCodeGenerator
                      text={`${import.meta.env.VITE_DOMAIN}/gallery/${
                        info.microsite_gallery_code
                      }`}
                    />
                  </Card>
                </>
              )}
            </React.Fragment>
          }
        />
        {!isProfile && [
          <Route
            path="campaigns"
            key="campaigns"
            element={
              <div>
                <CampaignManagement organizationId={id} />
              </div>
            }
          />,
          <Route
            path="contests"
            key="contests"
            element={
              <div>
                <ContestManagement organizationId={id} />
              </div>
            }
          />,
          <Route
            path="managers"
            key="managers"
            element={
              <div>
                <ManagerManagement organizationId={id} />
              </div>
            }
          />,
          <Route
            path="messages"
            key="messages"
            element={
              <div>
                <MessageList organizationId={id} />
              </div>
            }
          />,
          <Route
            path="keywords"
            key="keywords"
            element={
              <div>
                <KeywordManagement organizationId={id} />
              </div>
            }
          />,
          <Route
            path="efforts"
            key="efforts"
            element={
              <React.Fragment>
                <EffortManagement organizationId={id} showCreate={false} />
              </React.Fragment>
            }
          />,
          <Route
            path="locations"
            key="locations"
            element={
              <div>
                <LocationAssociate
                  organizationIds={[id]}
                  disabledIds={info.active_location_ids}
                  fetchData={() => fetchData()}
                  bulkData={{
                    show_location_variety_filter: true,
                    download_request_data: {
                      context: "org_add_locations",
                      organization_id: id,
                    },
                    upload_request_data: {
                      organization_id: id,
                      context: "add_org_loc",
                    },
                  }}
                />
              </div>
            }
          />,
          <Route
            path="location-groups"
            key="location-groups"
            element={
              <div>
                <LocationGroup organizationId={id} />
              </div>
            }
          />,
          <Route
            path="affiliates"
            key="affiliates"
            element={<AffiliateJoinManagement organizationId={id} />}
          />,
        ]}
        {isAdmin && [
          <Route
            path="bulk-communication"
            key="bulk-communication"
            element={
              <div>
                <BulkCommunicationManagement orgId={id} />
              </div>
            }
          />,
          <Route
            path="segments"
            key="segments"
            element={<UserGroupManagement orgId={id} />}
          />,
        ]}
        {showSms && (
          <Route
            path="sms-messaging"
            key="sms-messaging"
            element={
              <>
                <OrgSms info={info} fetchData={fetchData} orgId={id} />
              </>
            }
          />
        )}
        <Route
          path="asset-library"
          element={
            <React.Fragment>
              <AssetManagement organizationId={id} library />
            </React.Fragment>
          }
        />
        <Route
          path="loot-locker"
          element={
            <div>
              <LootLockerManagement orgId={id} />
            </div>
          }
        />
        <Route
          path="look-and-feel"
          element={
            <React.Fragment>
              <Tabs variant="outline" defaultValue="mixer" keepMounted={false}>
                <Tabs.List mr="xs">
                  <Tabs.Tab value="mixer">Mixer Settings</Tabs.Tab>
                  <Tabs.Tab value="email">Email Settings</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="mixer">
                  <EntitySettingsCode organizationId={id} />
                </Tabs.Panel>
                <Tabs.Panel value="email">
                  <EmailAppConfig
                    orgId={id}
                    name={
                      info?.settings_code?.settings?.settings?.email_branding
                        ?.entity_name || info.name
                    }
                    replyTo={
                      info?.settings_code?.settings?.settings?.email_branding
                        ?.reply_to
                    }
                    color={
                      info.settings_code &&
                      info.settings_code.settings &&
                      info.settings_code.settings.settings
                        ? info.settings_code.settings.settings.color
                        : "blue"
                    }
                  />
                </Tabs.Panel>
              </Tabs>
            </React.Fragment>
          }
        />
      </Routes>
    </div>
  );
}

const OrgSms = ({ fetchData, orgId, info }) => {
  const smsInfo = info.sms_info;

  const items = [
    // {
    //   title: "Transactional SMS Messaging",
    //   context: "transactional_sms",
    //   sms_info_key: "sms_transactional_info",
    //   allowed: info.allow_transactional_sms,
    //   ready: smsInfo?.sms_transactional_ready,
    //   active: smsInfo?.sms_transactional_active,
    // },
    // {
    //   title: "Conversational SMS Messaging",
    //   context: "conversational_sms",
    //   sms_info_key: "sms_conversational_info",
    //   allowed: info.allow_conversational_sms,
    //   ready: smsInfo?.sms_conversational_ready,
    //   active: smsInfo?.sms_conversational_active,
    // },
    {
      title: "SMS Marketing (where marketing messages are sent from)",
      context: "sms_marketing",
      sms_info_key: "sms_marketing_info",
      // allowed: smsInfo.can_activate_sms_marketing,
      allowed: info.allow_sms_marketing,
      ready: smsInfo?.sms_marketing_ready,
      active: smsInfo?.sms_marketing_active,
    },
    {
      title: "Double Opt-In",
      context: "sms_double_opt_in",
      sms_info_key: "sms_double_opt_in_info",
      allowed: info.allow_sms_marketing,
      ready: smsInfo?.sms_double_opt_in_ready,
      active: smsInfo?.sms_double_opt_in_active,
    },
  ].map((m) => ({
    ...m,
    messagingService: smsInfo?.[m.sms_info_key]?.messaging_service_ident
      ? smsInfo?.[m.sms_info_key]
      : null,
    sms: smsInfo?.[m.sms_info_key]?.phone_id ? smsInfo?.[m.sms_info_key] : null,
    reqData: {
      organization_id: orgId,
    },
  }));

  return (
    <div>
      {items.map((m, i) => (
        <Box key={i} mt="lg">
          <EntityMessagingSms fetchData={fetchData} {...m} />
        </Box>
      ))}
    </div>
  );
};
